import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  getUserEmailHistory,
  getUserEmailStats
} from '../../Redux/Action/EmailHistoryAction';
import EmailViewModal from './EmailViewModal';
import SendCustomEmailModal from './SendCustomEmailModal';
import EventHistoryModal from './EventHistoryModal';

const EmailHistorySection = ({ userId, userName, userEmail }) => {
  const dispatch = useDispatch();
  const {
    emails,
    emailsLoading,
    emailsError,
    emailsPagination,
    emailStats,
    emailStatsLoading
  } = useSelector((state) => state.emailHistory);

  const [showEmailModal, setShowEmailModal] = useState(false);
  const [selectedEmailId, setSelectedEmailId] = useState(null);
  const [showSendEmailModal, setShowSendEmailModal] = useState(false);
  const [showEventHistoryModal, setShowEventHistoryModal] = useState(false);
  const [selectedEmailForEvents, setSelectedEmailForEvents] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    if (userId) {
      dispatch(getUserEmailHistory({ userId, page: currentPage, limit: 10 }));
      dispatch(getUserEmailStats(userId));
    }
  }, [dispatch, userId, currentPage]);

  const handleViewEmail = (emailId) => {
    setSelectedEmailId(emailId);
    setShowEmailModal(true);
  };

  const handleViewEvents = (email) => {
    setSelectedEmailForEvents(email);
    setShowEventHistoryModal(true);
  };

  const handleSendCustomEmail = () => {
    setShowSendEmailModal(true);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const getStatusBadge = (status, events = []) => {
    const statusConfig = {
      sent: { color: '#6c757d', bg: '#e2e3e5', text: 'Sent', icon: 'fas fa-paper-plane' },
      delivered: { color: '#28a745', bg: '#d4edda', text: 'Delivered', icon: 'fas fa-check-circle' },
      bounced: { color: '#dc3545', bg: '#f8d7da', text: 'Bounced', icon: 'fas fa-exclamation-triangle' },
      failed: { color: '#dc3545', bg: '#f8d7da', text: 'Failed', icon: 'fas fa-times-circle' },
      pending: { color: '#ffc107', bg: '#fff3cd', text: 'Pending', icon: 'fas fa-clock' }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <span style={{
          padding: '6px 12px',
          borderRadius: '20px',
          fontSize: '12px',
          fontWeight: '600',
          color: config.color,
          backgroundColor: config.bg,
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          border: `1px solid ${config.color}20`
        }}>
          <i className={config.icon} style={{ fontSize: '10px' }}></i>
          {config.text}
        </span>
        {events.length > 0 && (
          <span style={{
            fontSize: '10px',
            color: '#6c757d',
            backgroundColor: '#f8f9fa',
            padding: '2px 6px',
            borderRadius: '10px',
            border: '1px solid #e9ecef'
          }}>
            {events.length} event{events.length !== 1 ? 's' : ''}
          </span>
        )}
      </div>
    );
  };

  const getCategoryBadge = (category) => {
    const categoryColors = {
      'OTP Verification': { bg: '#e3f2fd', color: '#1976d2', icon: 'fas fa-key' },
      'Booking Confirmation': { bg: '#e8f5e8', color: '#2e7d32', icon: 'fas fa-calendar-check' },
      'Booking Cancellation': { bg: '#ffebee', color: '#c62828', icon: 'fas fa-calendar-times' },
      'Password Reset': { bg: '#f3e5f5', color: '#7b1fa2', icon: 'fas fa-lock' },
      'Welcome': { bg: '#e0f2f1', color: '#00695c', icon: 'fas fa-hand-wave' },
      'Notification': { bg: '#fff3e0', color: '#ef6c00', icon: 'fas fa-bell' },
      'Custom': { bg: '#fce4ec', color: '#ad1457', icon: 'fas fa-edit' },
      'System': { bg: '#f5f5f5', color: '#424242', icon: 'fas fa-cog' },
      'Transactional': { bg: '#e8eaf6', color: '#3f51b5', icon: 'fas fa-receipt' }
    };

    const config = categoryColors[category] || categoryColors['System'];

    return (
      <span style={{
        padding: '4px 10px',
        borderRadius: '12px',
        fontSize: '11px',
        fontWeight: '600',
        color: config.color,
        backgroundColor: config.bg,
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        border: `1px solid ${config.color}20`
      }}>
        <i className={config.icon} style={{ fontSize: '9px' }}></i>
        {category}
      </span>
    );
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);
    
    if (diffInHours < 24) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffInHours < 168) { // 7 days
      return date.toLocaleDateString([], { weekday: 'short', hour: '2-digit', minute: '2-digit' });
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
    }
  };

  const getTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = (now - date) / (1000 * 60);
    
    if (diffInMinutes < 60) {
      return `${Math.floor(diffInMinutes)}m ago`;
    } else if (diffInMinutes < 1440) { // 24 hours
      return `${Math.floor(diffInMinutes / 60)}h ago`;
    } else {
      return `${Math.floor(diffInMinutes / 1440)}d ago`;
    }
  };

  if (emailsLoading && currentPage === 1) {
    return (
      <div style={{ 
        padding: '40px', 
        textAlign: 'center',
        backgroundColor: 'white',
        borderRadius: '12px',
        border: '1px solid #e9ecef'
      }}>
        <div style={{ 
          display: 'inline-block', 
          width: '40px', 
          height: '40px', 
          border: '3px solid #f3f3f3', 
          borderTop: '3px solid #007bff', 
          borderRadius: '50%', 
          animation: 'spin 1s linear infinite' 
        }}></div>
        <p style={{ marginTop: '16px', color: '#6c757d', fontSize: '14px' }}>Loading email history...</p>
      </div>
    );
  }

  return (
    <div style={{ marginBottom: '30px' }}>
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '24px',
        padding: '20px',
        backgroundColor: 'white',
        borderRadius: '12px',
        border: '1px solid #e9ecef',
        boxShadow: '0 2px 4px rgba(0,0,0,0.04)'
      }}>
        <div>
          <h4 style={{ 
            margin: '0 0 4px 0', 
            color: '#212529',
            fontSize: '18px',
            fontWeight: '600'
          }}>
            <i className="fas fa-envelope-open-text" style={{ marginRight: '8px', color: '#007bff' }}></i>
            Email Communication History
          </h4>
          <p style={{ 
            margin: 0, 
            fontSize: '14px', 
            color: '#6c757d',
            fontWeight: '400'
          }}>
            Track all email communications and delivery events for {userName}
          </p>
        </div>
        <button
          onClick={handleSendCustomEmail}
          style={{
            padding: '12px 20px',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            boxShadow: '0 2px 4px rgba(0,123,255,0.2)'
          }}
          onMouseOver={(e) => {
            e.target.style.backgroundColor = '#0056b3';
            e.target.style.transform = 'translateY(-1px)';
            e.target.style.boxShadow = '0 4px 8px rgba(0,123,255,0.3)';
          }}
          onMouseOut={(e) => {
            e.target.style.backgroundColor = '#007bff';
            e.target.style.transform = 'translateY(0)';
            e.target.style.boxShadow = '0 2px 4px rgba(0,123,255,0.2)';
          }}
        >
          <i className="fas fa-paper-plane"></i>
          Send Custom Email
        </button>
      </div>

      {/* Email Statistics */}
      {emailStats && !emailStatsLoading && (
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
          gap: '16px', 
          marginBottom: '24px' 
        }}>
          <div style={{ 
            padding: '20px', 
            backgroundColor: 'white', 
            borderRadius: '12px', 
            textAlign: 'center',
            border: '1px solid #e9ecef',
            boxShadow: '0 2px 4px rgba(0,0,0,0.04)',
            transition: 'transform 0.2s ease'
          }}
          onMouseOver={(e) => e.target.style.transform = 'translateY(-2px)'}
          onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
          >
            <div style={{ 
              fontSize: '32px', 
              fontWeight: '700', 
              color: '#007bff',
              marginBottom: '8px'
            }}>
              {emailStats.totalEmails || 0}
            </div>
            <div style={{ 
              fontSize: '14px', 
              color: '#6c757d',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px'
            }}>
              <i className="fas fa-envelope" style={{ color: '#007bff' }}></i>
              Total Emails
            </div>
          </div>
          
          <div style={{ 
            padding: '20px', 
            backgroundColor: 'white', 
            borderRadius: '12px', 
            textAlign: 'center',
            border: '1px solid #c3e6cb',
            boxShadow: '0 2px 4px rgba(40,167,69,0.1)',
            transition: 'transform 0.2s ease'
          }}
          onMouseOver={(e) => e.target.style.transform = 'translateY(-2px)'}
          onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
          >
            <div style={{ 
              fontSize: '32px', 
              fontWeight: '700', 
              color: '#28a745',
              marginBottom: '8px'
            }}>
              {(emailStats.sentEmails || 0) + (emailStats.deliveredEmails || 0)}
            </div>
            <div style={{ 
              fontSize: '14px', 
              color: '#155724',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px'
            }}>
              <i className="fas fa-check-circle" style={{ color: '#28a745' }}></i>
              Successful
            </div>
          </div>
          
          <div style={{ 
            padding: '20px', 
            backgroundColor: 'white', 
            borderRadius: '12px', 
            textAlign: 'center',
            border: '1px solid #f5c6cb',
            boxShadow: '0 2px 4px rgba(220,53,69,0.1)',
            transition: 'transform 0.2s ease'
          }}
          onMouseOver={(e) => e.target.style.transform = 'translateY(-2px)'}
          onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
          >
            <div style={{ 
              fontSize: '32px', 
              fontWeight: '700', 
              color: '#dc3545',
              marginBottom: '8px'
            }}>
              {(emailStats.failedEmails || 0) + (emailStats.bouncedEmails || 0)}
            </div>
            <div style={{ 
              fontSize: '14px', 
              color: '#721c24',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px'
            }}>
              <i className="fas fa-exclamation-triangle" style={{ color: '#dc3545' }}></i>
              Failed
            </div>
          </div>
          
          <div style={{ 
            padding: '20px', 
            backgroundColor: 'white', 
            borderRadius: '12px', 
            textAlign: 'center',
            border: '1px solid #ffeaa7',
            boxShadow: '0 2px 4px rgba(255,193,7,0.1)',
            transition: 'transform 0.2s ease'
          }}
          onMouseOver={(e) => e.target.style.transform = 'translateY(-2px)'}
          onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
          >
            <div style={{ 
              fontSize: '32px', 
              fontWeight: '700', 
              color: '#ffc107',
              marginBottom: '8px'
            }}>
              {emailStats.pendingEmails || 0}
            </div>
            <div style={{ 
              fontSize: '14px', 
              color: '#856404',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px'
            }}>
              <i className="fas fa-clock" style={{ color: '#ffc107' }}></i>
              Pending
            </div>
          </div>
        </div>
      )}

      {/* Email List */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        border: '1px solid #e9ecef',
        boxShadow: '0 2px 4px rgba(0,0,0,0.04)',
        overflow: 'hidden'
      }}>
        {emailsError ? (
          <div style={{ 
            padding: '40px', 
            backgroundColor: '#f8d7da', 
            color: '#721c24', 
            textAlign: 'center',
            border: '1px solid #f5c6cb'
          }}>
            <i className="fas fa-exclamation-triangle" style={{ fontSize: '24px', marginBottom: '12px' }}></i>
            <p style={{ margin: 0, fontWeight: '500' }}>Error loading email history: {emailsError}</p>
          </div>
        ) : emails.length === 0 ? (
          <div style={{ 
            padding: '60px 40px', 
            textAlign: 'center', 
            color: '#6c757d'
          }}>
            <i className="fas fa-inbox" style={{ fontSize: '48px', marginBottom: '16px', color: '#dee2e6' }}></i>
            <h5 style={{ margin: '0 0 8px 0', color: '#495057' }}>No emails found</h5>
            <p style={{ margin: 0, fontSize: '14px' }}>No email communications have been sent to this user yet.</p>
          </div>
        ) : (
          <div>
            {emails.map((email, index) => (
              <div 
                key={email._id} 
                style={{ 
                  padding: '20px',
                  borderBottom: index < emails.length - 1 ? '1px solid #f8f9fa' : 'none',
                  transition: 'background-color 0.2s ease',
                  cursor: 'pointer'
                }}
                onMouseOver={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                onMouseOut={(e) => e.target.style.backgroundColor = 'transparent'}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px' }}>
                  {/* Email Content */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                      <h6 style={{ 
                        margin: 0, 
                        fontSize: '16px', 
                        fontWeight: '600', 
                        color: '#212529',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        flex: 1
                      }}>
                        {email.email.subject}
                      </h6>
                      {getCategoryBadge(email.email.category)}
                    </div>
                    
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '12px' }}>
                      <div style={{ fontSize: '13px', color: '#6c757d' }}>
                        <i className="fas fa-user" style={{ marginRight: '4px', color: '#007bff' }}></i>
                        {email.sender.name}
                      </div>
                      <div style={{ fontSize: '13px', color: '#6c757d' }}>
                        <i className="fas fa-clock" style={{ marginRight: '4px', color: '#6c757d' }}></i>
                        {getTimeAgo(email.createdAt)}
                      </div>
                      {email.delivery.deliveredAt && (
                        <div style={{ fontSize: '13px', color: '#28a745' }}>
                          <i className="fas fa-check" style={{ marginRight: '4px' }}></i>
                          Delivered {formatDate(email.delivery.deliveredAt)}
                        </div>
                      )}
                    </div>
                    
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      {getStatusBadge(email.delivery.status, email.delivery.events || [])}
                      
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewEmail(email._id);
                          }}
                          style={{
                            padding: '8px 16px',
                            backgroundColor: '#007bff',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '12px',
                            fontWeight: '500',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}
                          onMouseOver={(e) => {
                            e.target.style.backgroundColor = '#0056b3';
                            e.target.style.color = 'white';
                          }}
                          onMouseOut={(e) => {
                            e.target.style.backgroundColor = '#007bff';
                            e.target.style.color = 'white';
                          }}
                        >
                          <i className="fas fa-eye" style={{ fontSize: '10px' }}></i>
                          View
                        </button>
                        
                        {email.delivery.events && email.delivery.events.length > 0 && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewEvents(email);
                            }}
                            style={{
                              padding: '8px 16px',
                              backgroundColor: '#28a745',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              fontSize: '12px',
                              fontWeight: '500',
                              cursor: 'pointer',
                              transition: 'all 0.2s ease',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}
                            onMouseOver={(e) => {
                              e.target.style.backgroundColor = '#1e7e34';
                              e.target.style.color = 'white';
                            }}
                            onMouseOut={(e) => {
                              e.target.style.backgroundColor = '#28a745';
                              e.target.style.color = 'white';
                            }}
                          >
                            <i className="fas fa-history" style={{ fontSize: '10px' }}></i>
                            Events ({email.delivery.events.length})
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Pagination */}
      {emailsPagination.totalPages > 1 && (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          marginTop: '24px',
          gap: '12px'
        }}>
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            style={{
              padding: '10px 16px',
              backgroundColor: currentPage === 1 ? '#f8f9fa' : '#007bff',
              color: currentPage === 1 ? '#6c757d' : 'white',
              border: '1px solid #e9ecef',
              borderRadius: '8px',
              cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
              fontWeight: '500',
              transition: 'all 0.2s ease'
            }}
          >
            <i className="fas fa-chevron-left" style={{ marginRight: '6px' }}></i>
            Previous
          </button>
          
          <div style={{ 
            padding: '10px 16px',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: '500',
            color: '#495057'
          }}>
            Page {currentPage} of {emailsPagination.totalPages}
          </div>
          
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === emailsPagination.totalPages}
            style={{
              padding: '10px 16px',
              backgroundColor: currentPage === emailsPagination.totalPages ? '#f8f9fa' : '#007bff',
              color: currentPage === emailsPagination.totalPages ? '#6c757d' : 'white',
              border: '1px solid #e9ecef',
              borderRadius: '8px',
              cursor: currentPage === emailsPagination.totalPages ? 'not-allowed' : 'pointer',
              fontWeight: '500',
              transition: 'all 0.2s ease'
            }}
          >
            Next
            <i className="fas fa-chevron-right" style={{ marginLeft: '6px' }}></i>
          </button>
        </div>
      )}

      {/* Modals */}
      <EmailViewModal
        show={showEmailModal}
        emailId={selectedEmailId}
        onClose={() => {
          setShowEmailModal(false);
          setSelectedEmailId(null);
        }}
      />

      <SendCustomEmailModal
        show={showSendEmailModal}
        userId={userId}
        userName={userName}
        userEmail={userEmail}
        onClose={() => setShowSendEmailModal(false)}
        onSuccess={() => {
          setShowSendEmailModal(false);
          // Refresh email history
          dispatch(getUserEmailHistory({ userId, page: currentPage, limit: 10 }));
          dispatch(getUserEmailStats(userId));
        }}
      />

      <EventHistoryModal
        show={showEventHistoryModal}
        events={selectedEmailForEvents?.delivery?.events || []}
        emailSubject={selectedEmailForEvents?.email?.subject || ''}
        onClose={() => {
          setShowEventHistoryModal(false);
          setSelectedEmailForEvents(null);
        }}
      />

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default EmailHistorySection;